import {
  AppsSection,
  needsRedirectWelcomeHub,
  Notification,
  NotificationsMenuParameters,
  NotificationStatus,
  WELCOME_HUB_SHOWN_COOKIE_NAME,
} from '@eposnow/ui-core';
import { Box, CircularProgress } from '@mui/material';
import * as Sentry from '@sentry/react';
import enLocale from 'date-fns/locale/en-GB';
import { useAuth } from 'oidc-react';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useCookies } from 'react-cookie';
import { useTranslation } from 'react-i18next';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createSearchParams } from 'react-router-dom';
import type { PostHog } from 'posthog-js';
import { apiFetch as apiFetchFn, ErrorObject } from '../api/fetch';
import AuthenticatingSpinner from '../components/AuthenticatingSpinner';
import ErrorLoadingData from '../components/ErrorLoadingData';
import { NOTIFICATIONS_PER_PAGE } from '../constants';
import { isSubLogin, skipSentry } from '../helpers/helpers';
import { useApiQuery } from '../helpers/useApi';
import localesMap from '../i18n/localesMap.json';
import {
  GraduationStatus,
  InstalledApp,
  LabsFeature,
  SubuserRightsResponse,
  User,
  UserAccessRights,
  UserRole,
} from '../types';
import { UIContext } from './UIContext';
// import { getChromeMajorVersion } from '../helpers/useBrowserInfo'; TODO # uncomment when Posthog is re-enabled

const DEFAULT_LOCALE = 'en-GB';

/* eslint-enable @typescript-eslint/no-empty-function */
const UserContext = React.createContext({
  apiFetch: apiFetchFn,
  user: undefined as User | undefined,
  locale: DEFAULT_LOCALE,
  localeFns: enLocale,
  installedApps: [] as InstalledApp[] | undefined,
  userModules: [] as UserRole[] | undefined,
  labsFeatures: [] as LabsFeature[] | undefined,
  userAccessRights: {} as UserAccessRights,
  isLoadingUserApps: false,
  appsMenu: [] as AppsSection[][],
  cdnUrl: undefined as string | undefined,
  isErrorAppsMenu: false,
  refetchAppsMenu: () => {
    /* no op */
  },
  notificationsMenuParameters: {} as NotificationsMenuParameters,
});

export type NotificationsPagedApiResponse = {
  data: Notification[];
  metadata: {
    page: number;
    limit: number;
    totalPages: number;
    totalRecords: number;
    currentPageSize: number;
    adHocMetadata: {
      unreadRecords: number;
    };
  };
  _links: {
    baseURL: string;
    firstPage: string;
    lastPage: string;
    nextPage: string | null;
    previousPage: string | null;
  };
};

export type AppsMenuResponse = {
  baseUrl: string;
  items: AppsSection[][];
};

const UserContextProvider = ({ children }: { children: JSX.Element }) => {
  const auth = useAuth();
  const { t, i18n } = useTranslation();
  const { setColorModeFromBoolean, setAnimationsDisabledFromBoolean } = useContext(UIContext);
  const [locale, setLocale] = useState(DEFAULT_LOCALE);
  const [localeFns, setLocaleFns] = useState<Locale>(enLocale);
  const [localeSet, setLocaleSet] = useState(false);
  const [cookies, setCookie] = useCookies([
    'useDarkTheme',
    'disableAnimation',
    WELCOME_HUB_SHOWN_COOKIE_NAME,
  ]);
  const [cdnUrl, setCdnUrl] = useState('');
  const [appsMenu, setAppsMenu] = useState<AppsSection[][]>([]);

  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [notificationsUnreadCount, setNotificationsUnreadCount] = useState<number>(0);

  const queryClient = useQueryClient();

  const maybeAccessToken = auth?.userData?.access_token;

  // const [posthog, setPosthog] = useState<PostHog | null>(null);

  // useEffect(() => {
  //   const chromeMajorVersion = getChromeMajorVersion();
  //   const supportsPostHog = chromeMajorVersion === null || chromeMajorVersion >= 49;
  //   if (supportsPostHog) {
  //     import('posthog-js').then(({ default: ph }) => setPosthog(ph)).catch(() => undefined);
  //   }
  // }, []);

  // So creating a generic as <T> in this context makes the parser think this is a JSX tag instead of a generic
  // The trick is adding an extends rule but then  ESLint says that it is unnecessary to extend from unknown
  // Since we don't have a superclass holding all our entities, I have to disable this rule
  // So sorry about that.
  const apiFetch = useCallback(
    /* eslint-disable-next-line @typescript-eslint/no-unnecessary-type-constraint */
    <T extends unknown>(
      path = '/',
      body: unknown = {},
      method = 'GET',
      endpoint: string | undefined = process.env.REACT_APP_API_GATEWAY,
      authorization: string | undefined = undefined,
      plain = false,
      extraHeaders: Record<string, string> = {},
      returnFullResponse = false
    ) =>
      apiFetchFn<T>(
        path,
        body,
        method,
        endpoint,
        authorization || `Bearer ${maybeAccessToken}`,
        plain,
        extraHeaders,
        returnFullResponse
      ),
    [maybeAccessToken]
  );

  const importLocaleFile = async (lang: string) => {
    let localeToSet;
    try {
      localeToSet = await import(`date-fns/locale/${lang}/index.js`);
    } catch {
      localeToSet = await import(`date-fns/locale/${lang.substring(0, 2)}/index.js`);
    }
    if (!localeToSet) return Promise.reject(new Error('Locale not found'));
    setLocaleFns(localeToSet.default);
    return Promise.resolve();
  };

  const formatAccessRights = useCallback(
    (isSublogin: boolean, accessRights: SubuserRightsResponse | undefined): UserAccessRights => {
      if (!isSublogin) {
        return {
          Billing: true,
          LocationAreaID: null,
          Setup: true,
          Management: true,
          PurchaseOrderCreate: true,
          PurchaseOrderEditCancel: true,
          PurchaseOrderReceive: true,
          Product: true,
          Reporting: true,
          Margin: true,
          Till: true,
          WebIntegration: true,
          Apps: true,
          GlobalCustomer: true,
          Franchise: true,
        };
      }

      if (!accessRights) {
        return {};
      }

      return {
        Billing: accessRights.BillingRights || false,
        LocationAreaID: accessRights.AccessRights.LocationAreaID,
        Setup: accessRights.AccessRights.Setup || false,
        Management: accessRights.AccessRights.Management || false,
        PurchaseOrderCreate: accessRights.AccessRights.PurchaseOrder.Create || false,
        PurchaseOrderEditCancel: accessRights.AccessRights.PurchaseOrder.EditCancel || false,
        PurchaseOrderReceive: accessRights.AccessRights.PurchaseOrder.Receive || false,
        Product: accessRights.AccessRights.Product || false,
        Reporting: accessRights.AccessRights.Reporting || false,
        Margin: accessRights.AccessRights.Margin || false,
        Till: accessRights.AccessRights.Till || false,
        WebIntegration: accessRights.AccessRights.WebIntegration || false,
        Apps: accessRights.AccessRights.Apps || false,
        GlobalCustomer: accessRights.AccessRights.GlobalCustomer || false,
        Franchise: accessRights.AccessRights.Franchise || false,
      };
    },
    []
  );

  const {
    refetch: fetchUser,
    data: user,
    isLoading,
    isError,
    error,
  } = useApiQuery<User>(
    {
      path: `user`,
      body: undefined,
      endpoint: process.env.REACT_APP_API_V1,
    },
    'userData'
  );

  // Setup Posthog Reporting
  // useEffect(() => {
  //   if (!user || !posthog) return;

  //   try {
  //     posthog.identify(user.id, {
  //       name: user.fullName,
  //       userName: user.userName,
  //       email: user.email,
  //       location: user.location,
  //       locale: user.locale,
  //       isEmailVerified: user.isEmailVerified,
  //       isTwoFactorEnabled: user.isTwoFactorEnabled,
  //     });

  //     const environment = process.env.REACT_APP_SENTRY_ENVIRONMENT;
  //     const isProduction = environment === 'production';
  //     const isEposNowEmail = user.email && user.email.endsWith('@eposnow.com');

  //     // Start session recording based on environment and email domain
  //     // In production: only record if email is NOT @eposnow.com
  //     // In alpha/test: record all sessions regardless of email domain
  //     if (!isProduction || (isProduction && !isEposNowEmail)) {
  //       posthog.startSessionRecording();
  //     }
  //   } catch (e) {
  //     Sentry.captureException(new Error('Error Identifying User In PostHog'), {
  //       extra: { error: JSON.stringify(e) },
  //     });
  //   }
  // }, [user, posthog]);

  const { data: installedApps, isLoading: isLoadingInstalledApps } = useApiQuery<InstalledApp[]>(
    {
      path: `salesforce/appdetails/installedapps/${auth?.userData?.profile.parent_guid}`,
      body: undefined,
      endpoint: process.env.REACT_APP_API_V1,
    },
    'installedApps',
    {
      enabled: !!auth?.userData?.profile?.parent_guid,
    }
  );

  const { data: userModules, isLoading: isLoadingUserModules } = useApiQuery<UserRole[]>(
    {
      path: 'user/modules',
      body: undefined,
      endpoint: process.env.REACT_APP_API_V4,
    },
    'userModules'
  );

  const { data: labsFeatures, isLoading: isLoadingLabsFeatures } = useApiQuery<LabsFeature[]>(
    {
      path: 'Feature',
      body: undefined,
      endpoint: process.env.REACT_APP_API_V4,
    },
    'labsFeatures'
  );

  const { data: userRights, isLoading: isLoadingAccessRights } = useApiQuery<SubuserRightsResponse>(
    {
      path: `subuser/${auth?.userData?.profile.sub}`,
      body: undefined,
      endpoint: process.env.REACT_APP_API_V4,
    },
    'userRights',
    {
      enabled:
        !auth?.isLoading &&
        auth?.userData?.profile?.sub?.toLowerCase() !==
          (auth?.userData?.profile?.parent_guid as string)?.toLowerCase(),
    }
  );

  const { data: uiPreferences } = useApiQuery<{ useDarkTheme: boolean; disableAnimation: boolean }>(
    {
      path: `users/${auth?.userData?.profile.sub}/ui-preferences`,
      body: undefined,
      endpoint: process.env.REACT_APP_API_V1,
    },
    'uiPreferences'
  );

  useEffect(() => {
    if (uiPreferences) {
      setCookie('useDarkTheme', uiPreferences.useDarkTheme);
      setCookie('disableAnimation', uiPreferences.disableAnimation);
    }
    // Apply preferences to UI (use API data if available, otherwise fall back to cookies)
    setColorModeFromBoolean(uiPreferences?.useDarkTheme || cookies.useDarkTheme);
    setAnimationsDisabledFromBoolean(uiPreferences?.disableAnimation || cookies.disableAnimation);
  }, [
    uiPreferences,
    cookies.useDarkTheme,
    cookies.disableAnimation,
    setCookie,
    setColorModeFromBoolean,
    setAnimationsDisabledFromBoolean,
  ]);

  const {
    data: appsMenuData,
    isLoading: isLoadingUserApps,
    isError: isErrorAppsMenu,
    refetch: refetchAppsMenu,
  } = useQuery({
    queryKey: ['userApps'],
    queryFn: () =>
      apiFetch<AppsMenuResponse>(
        `v1/apps`,
        undefined,
        'GET',
        process.env.REACT_APP_API_IDENTITY,
        undefined,
        false,
        {
          'Accept-Language': user!.locale,
        }
      ),
    enabled: !!auth?.userData?.access_token && !!user,
  });

  useEffect(() => {
    if (appsMenuData) {
      setCdnUrl(appsMenuData.baseUrl);
      setAppsMenu(appsMenuData.items);
    }
  }, [appsMenuData]);

  const {
    data: notificationsData,
    isError: isErrorUserNotifications,
    fetchNextPage,
    hasNextPage,
    isLoading: isLoadingNotifications,
    refetch: refetchUserNotifications,
    error: notificationsError,
  } = useInfiniteQuery({
    queryKey: ['userNotifications'],
    queryFn: async ({ pageParam }: { pageParam: number }) => {
      const pagination = createSearchParams({
        page: String(pageParam),
        limit: String(NOTIFICATIONS_PER_PAGE),
      });
      return apiFetch<NotificationsPagedApiResponse>(
        `v1/notification/in-app-notifications${pagination ? `?${pagination}` : ''}`,
        undefined,
        'GET',
        process.env.REACT_APP_API_IDENTITY,
        undefined,
        false,
        undefined,
        true
      );
    },
    initialPageParam: 1,
    getNextPageParam: (
      lastPage: NotificationsPagedApiResponse,
      allPages: NotificationsPagedApiResponse[]
    ) => {
      if (!lastPage?.metadata?.totalPages) return undefined;
      const nextPage = (allPages?.length || 0) + 1;
      return lastPage.metadata.totalPages >= nextPage ? nextPage : undefined;
    },
    enabled: !!auth?.userData?.access_token && !!user,
    staleTime: 0,
  });

  useEffect(() => {
    if (notificationsData?.pages?.length) {
      const lastPage = notificationsData.pages[
        notificationsData.pages.length - 1
      ] as NotificationsPagedApiResponse;
      if (lastPage?.data) {
        const allNotifications = notificationsData.pages.reduce<Notification[]>(
          (acc: Notification[], page: NotificationsPagedApiResponse) =>
            acc.concat(page?.data || []),
          []
        );
        setNotifications(allNotifications);
        setNotificationsUnreadCount(lastPage?.metadata?.adHocMetadata?.unreadRecords || 0);
      }
    }
  }, [notificationsData]);

  useEffect(() => {
    if (isErrorUserNotifications && notificationsError) {
      const err = notificationsError as ErrorObject;
      if (!skipSentry(err)) {
        Sentry.captureException(new Error(`Error loading notifications`), {
          extra: { error: JSON.stringify(err) },
        });
      }
    }
  }, [isErrorUserNotifications, notificationsError]);

  const onLoadMoreNotificationsAsync = useCallback(async () => {
    if (hasNextPage) await fetchNextPage();
  }, [hasNextPage, fetchNextPage]);

  const {
    mutateAsync: onReadNotification,
    isError: isReadNotificationError,
    error: readNotificationError,
  } = useMutation({
    mutationFn: async ({ notification }: { notification: Notification }) => {
      const response = await apiFetch(
        `${process.env.REACT_APP_API_IDENTITY}v1/notification/in-app-notifications/${notification.notificationId}`,
        [
          {
            op: 'replace',
            path: '/status',
            value: NotificationStatus.Read,
          },
        ],
        'PATCH'
      );

      return response;
    },
  });

  useEffect(() => {
    if (isReadNotificationError && readNotificationError) {
      const err = readNotificationError as ErrorObject;
      if (!skipSentry(err)) {
        Sentry.captureException(new Error(`Error reading notification`), {
          extra: { error: JSON.stringify(err) },
        });
      }
    }
  }, [isReadNotificationError, readNotificationError]);

  const onReadNotificationAsync = useCallback(
    async (notification: Notification) => {
      if (!notification || notification.status !== NotificationStatus.Created) return;

      // This update is necessary to immediately reflect the change in the notification's status
      // within the React Query cache without waiting for a refetch. When a notification is marked
      // as "Read", we need to update the corresponding notification in our cached "userNotifications"
      // data to ensure the UI displays the correct state. Additionally, we decrement the unread
      // notifications count (ensuring it doesn’t go below zero) in the metadata. This way, both the
      // list of notifications and the unread count stay in sync with the user's action.
      queryClient.setQueryData(['userNotifications'], (oldData: any) => {
        if (!oldData?.pages) return oldData;
        return {
          ...oldData,
          pages: oldData.pages.map((page: any) => ({
            ...page,
            data:
              page?.data?.map((n: Notification) =>
                n?.notificationId === notification.notificationId
                  ? { ...n, status: NotificationStatus.Read }
                  : n
              ) || [],
            metadata: {
              ...page?.metadata,
              adHocMetadata: {
                ...page?.metadata?.adHocMetadata,
                unreadRecords: Math.max((page?.metadata?.adHocMetadata?.unreadRecords || 0) - 1, 0),
              },
            },
          })),
        };
      });
      await onReadNotification({ notification });
    },
    [onReadNotification]
  );

  const {
    mutateAsync: onDeleteNotification,
    isError: isDeleteNotificationError,
    error: deleteNotificationError,
  } = useMutation({
    mutationFn: async ({ notification }: { notification: Notification }) => {
      const response = await apiFetch(
        `${process.env.REACT_APP_API_IDENTITY}v1/notification/in-app-notifications/${notification.notificationId}`,
        [
          {
            op: 'replace',
            path: '/status',
            value: NotificationStatus.Dismissed,
          },
        ],
        'PATCH'
      );

      return response;
    },
  });

  useEffect(() => {
    if (isDeleteNotificationError && deleteNotificationError) {
      const err = deleteNotificationError as ErrorObject;
      if (!skipSentry(err)) {
        Sentry.captureException(new Error(`Error deleting notification`), {
          extra: { error: JSON.stringify(err) },
        });
      }
    }
  }, [isDeleteNotificationError, deleteNotificationError]);

  const onDeleteNotificationAsync = useCallback(
    async (notification: Notification) => {
      setNotifications((prev) =>
        prev.filter((n) => n.notificationId !== notification.notificationId)
      );

      if (notification.status === NotificationStatus.Created) {
        setNotificationsUnreadCount((prevCount) => Math.max(prevCount - 1, 0));
      }

      await onDeleteNotification({ notification });

      if (hasNextPage) {
        queryClient.removeQueries({ queryKey: ['userNotifications'] });
      }
    },
    [hasNextPage, onDeleteNotification]
  );

  const { data: graduationStatus } = useApiQuery<GraduationStatus>(
    {
      path: `organisations/${auth?.userData?.profile?.sub}/graduation-status`,
      body: undefined,
      endpoint: process.env.REACT_APP_API_V1,
    },
    'graduationStatus',
    {
      enabled:
        !auth?.isLoading &&
        auth?.userData?.profile?.sub?.toLowerCase() ===
          (auth?.userData?.profile?.parent_guid as string)?.toLowerCase(),
    }
  );

  useEffect(() => {
    if (auth?.userData && graduationStatus) {
      const redirectToWelcomeHub = needsRedirectWelcomeHub(
        cookies,
        setCookie,
        auth?.userData?.profile?.sub?.toLowerCase() !==
          (auth?.userData?.profile?.parent_guid as string)?.toLowerCase(),
        graduationStatus?.isGraduated
      );
      if (redirectToWelcomeHub) {
        window.location.replace(`${process.env.REACT_APP_LINK_ORGANISATIONS}welcome-hub`);
      }
    }
  }, [auth?.userData, cookies, setCookie, graduationStatus]);

  if (user && !localeSet) {
    let lang = user.locale;
    const locales = localesMap.locales as Record<string, string>;
    if (!(lang in locales)) lang = 'en-GB';

    setLocale(locales[lang]);
    setLocaleSet(true);
    i18n.changeLanguage(locales[lang]).then(() => {
      importLocaleFile(locales[lang]).then();
    });
  }

  if (window.location.pathname.includes('logged-out')) return null;

  if (auth && auth.userData) {
    if (isError && (error as any)?.code === 401) return <AuthenticatingSpinner />;
    if (isError) return <ErrorLoadingData action={fetchUser} />;
    const isLoadingUserAndPermissions =
      isLoadingUserModules ||
      isLoadingLabsFeatures ||
      isLoadingInstalledApps ||
      isLoadingAccessRights ||
      isLoading;
    if (isLoadingUserAndPermissions || !user) {
      return (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            margin: '96px',
          }}
        >
          <CircularProgress
            aria-label="Loading User Data"
            aria-live="polite"
            aria-busy={isLoadingUserAndPermissions || !user}
            data-testid="loading-icon"
            sx={{ color: 'text.secondary' }}
          />
        </Box>
      );
    }

    const userAccessRights = formatAccessRights(
      isSubLogin(auth?.userData?.profile?.sub, auth?.userData?.profile?.parent_guid as string),
      userRights
    );
    return (
      <UserContext.Provider
        value={{
          user,
          apiFetch,
          locale,
          localeFns,
          userModules,
          labsFeatures,
          installedApps,
          userAccessRights,
          isLoadingUserApps,
          cdnUrl,
          appsMenu,
          isErrorAppsMenu,
          refetchAppsMenu,
          notificationsMenuParameters: {
            isError: isErrorUserNotifications,
            notifications,
            notificationsUnreadCount,
            isLoadingNotifications,
            hasMoreNotificationsToLoad: hasNextPage ?? false,
            onDeleteNotificationAsync,
            onReadNotificationAsync,
            onLoadMoreNotificationsAsync,
            onRetryFetchNotifications: refetchUserNotifications,
          },
        }}
      >
        {children}
      </UserContext.Provider>
    );
  }

  if (auth.isLoading) {
    return <AuthenticatingSpinner />;
  }

  return null;
};

export { UserContext, UserContextProvider };
